import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useOrganization } from "../context/OrganizationContext";
import { getBillingAccess } from "../modules/upload-inbox/api";
import "./SubscriptionGate.css";

/** Pages a locked org's owner can still open: the plan picker and their profile. */
const OWNER_ALLOWED_PATHS = ["/billing", "/profile"];

/**
 * Plan picker at sign-up. Organizations created after 2026-10-28 must
 * subscribe before using the app (the API returns 402 on tenant routes
 * until then; requireSubscriptionWhenRequired). This sends the owner to
 * /billing to choose a plan, and tells other members to ask their owner.
 *
 * Re-checked on every navigation, so the app unlocks as soon as the
 * subscription webhook lands. A failed check renders the app: the API is
 * the boundary, this is only routing.
 */
export function SubscriptionGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.id;
  const [required, setRequired] = useState<boolean | null>(null);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    getBillingAccess()
      .then((res) => !cancelled && setRequired(res.subscriptionRequired))
      .catch(() => !cancelled && setRequired(false));
    return () => {
      cancelled = true;
    };
  }, [orgId, location.pathname]);

  if (required === null) {
    return <div className="subscription-gate__loading">Loading…</div>;
  }
  if (!required) return <>{children}</>;

  if (activeOrganization?.role === "OWNER") {
    if (OWNER_ALLOWED_PATHS.includes(location.pathname)) return <>{children}</>;
    return <Navigate to="/billing" replace />;
  }

  if (location.pathname === "/profile") return <>{children}</>;
  return (
    <div className="subscription-gate__blocked" role="status">
      <h2>A subscription is needed</h2>
      <p>
        {activeOrganization?.name ?? "This organization"} doesn't have an active plan yet.
        Ask the organization owner to choose one on the Billing page.
      </p>
    </div>
  );
}
